'use client'
import { Teams } from '@glovo/organisation'
import React, { useEffect, useRef, useState } from 'react'
import type { FC, FormEvent } from 'react'
import styles from './AddressDetailsForm.module.scss'
import { BuildingDetails } from './building-details/BuildingDetails'
import { DeleteAddressModal } from './delete-address-modal/DeleteAddressModal'
import { DeleteAddressSnackbar } from './delete-address-snackbar/DeleteAddressSnackbar'
import { RefineEntrance } from './refine-entrance/RefineEntrance'
import { SaveAddressAs } from './save-address-as/SaveAddressAs'
import type { FormData } from './types'
import type { AddressSourceValues } from '@/tools/analytics/events/domains/address-book'
import { useAuth } from '@/tools/auth'
import { useLocalisation } from '@/tools/i18n/client'
import { glovoRum, useErrorHandling, withErrorBoundary } from '@/tools/o11y/client'
import {
  Button,
  ButtonVariants,
  ChevronBackwardIcon,
  IconButton,
  IconButtonSize,
  IconButtonVariants,
} from '@/tools/ui'
import { LoaderWrapper } from '@/tools/ui'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'
import { addressCreationSucceededEvent, addressDetailsEvent } from '@/user/address-book/analytics'
import type {
  UserAddress,
  UserAddressFieldType,
  UserAddressNew,
} from '@/user/address-book/entities'
import { TitleDesktop } from '@/user/address-book/ui/components/title-desktop/TitleDesktop'
import {
  BUILDING_DETAILS_FIELD_OPTIONS,
  BUILDING_DETAILS_FIELD_ORDER,
} from '@/user/address-book/ui/constants'
import {
  createUserAddress,
  deleteUserAddress,
  getUserAddresses,
  updateUserAddress,
} from '@/user/address-book/use-cases'

const AddressDetailsFormComponent: FC = () => {
  const { execWithErrorTracking } = useErrorHandling()
  const { isLoggedIn } = useAuth()
  const { t } = useLocalisation()
  const {
    addressData,
    navigate,
    setAddress,
    selectAddress,
    hasRefinedEntrance,
    addressFormTrigger,
    isMobile,
    entrySource,
    deviceCurrentLocation: { deviceLocation },
  } = useAddressBookContext()
  const isEditing = 'id' in addressData
  const fieldOptions = BUILDING_DETAILS_FIELD_OPTIONS[addressData.kind]
  const fieldOrder = BUILDING_DETAILS_FIELD_ORDER[addressData.kind]

  const buildingDetailsRef = useRef<HTMLDivElement>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isAddressMissingTag, setIsAddressMissingTag] = useState<boolean>(false)
  const [formData, setFormData] = useState<FormData>({
    fields: fieldOptions.map((option) => {
      const existingAddressDataField = addressData.fields.find(
        (field) => field.type === option.type
      )
      const value = existingAddressDataField?.value ?? ''
      return {
        ...option,
        value,
        error: '',
      }
    }),
    addressTag: addressData.tag,
  })

  const validateForm = () => {
    const updatedFields = formData.fields.map((field) => ({
      ...field,
      error: field.required && field.value === '' ? t('common.required') : '',
    }))

    const isMissingCustomTag = formData.addressTag === ''
    setIsAddressMissingTag(isMissingCustomTag)

    setFormData((formData) => ({
      ...formData,
      fields: updatedFields,
    }))

    return updatedFields.every((field) => !field.error) && !isMissingCustomTag
  }

  const createAddressDetails = (address: UserAddress | UserAddressNew): string => {
    const details = fieldOrder
      .map((fieldType) => address.fields.find(({ type }) => type === fieldType)?.value)
      .filter((field) => field !== undefined)
      .join(', ')
    return details
  }

  const deleteAddress = async (id: UserAddress['id']) => {
    await deleteUserAddress(id)
  }

  const getAddressInput = (): UserAddress | UserAddressNew => ({
    ...addressData,
    tag: formData.addressTag,
    fields: formData.fields
      .map(({ type, value }) => ({ type, value }))
      .filter(({ value }) => value !== ''),
  })

  const handleFieldChange = (fieldType: UserAddressFieldType, value: string) => {
    setFormData({
      ...formData,
      fields: formData.fields.map((prevField) =>
        prevField.type === fieldType ? { ...prevField, value } : prevField
      ),
    })
  }

  const handleTagChange = (tag: string) => {
    setFormData((formData) => ({ ...formData, addressTag: tag }))
  }

  const debugInvalidAddress = async (address: UserAddress) => {
    try {
      if (address.latitude === 0 && address.longitude === 0) {
        glovoRum.addAction(
          'user.address_book.step.address_details_form.edit_address.invalid_coords',
          {
            address: address,
            addresses: await getUserAddresses(),
          }
        )
      }
    } finally {
      return
    }
  }

  const handleEditAddress = async (address: UserAddress) => {
    // NOTE: Addresses with invalid coordinates have been reported on address update flow
    // the following method keeps track of those cases
    await debugInvalidAddress(address)

    await updateUserAddress(address)
    addressCreationSucceededEvent(
      {
        address,
        deviceLocation: Boolean(deviceLocation),
        hasRefinedEntrance,
        addressFormTrigger: addressFormTrigger ?? 'PinOnMap',
      },
      'EditDeliveryInfo',
      entrySource
    )

    navigate('onBackToAddressList')
  }

  const getAddressSourceFromAddressFormTrigger = (): AddressSourceValues => {
    switch (addressFormTrigger) {
      case 'SavedAddressEdit':
        return 'PastDeliveryAddress'
      case 'PinOnMap':
        return 'PinOnMap'
      case 'Search':
        return 'SearchResult'
      case 'UnsavedCurrentLocation':
        return 'PinOnMap'
      default:
        return 'PinOnMap'
    }
  }

  const handleCreateAddress = async (address: UserAddressNew) => {
    if (isLoggedIn) {
      await createUserAddress(address)
    }
    addressCreationSucceededEvent(
      {
        address,
        deviceLocation: Boolean(deviceLocation),
        hasRefinedEntrance,
        addressFormTrigger: addressFormTrigger ?? 'PinOnMap',
      },
      getAddressSourceFromAddressFormTrigger(),
      entrySource
    )
    selectAddress({
      ...address,
      details: createAddressDetails(address),
    })
  }

  const handleNavigateBack = () => {
    isEditing ? navigate('onBackToAddressList') : navigate('onBack')
  }

  const handleNavigationToSetEntrance = () => {
    const addressInput = getAddressInput()
    setAddress(addressInput)
    navigate('onSetEntrance')
  }

  const handleDelete = async () => {
    if (!isEditing) {
      return
    }
    setIsDeleting(true)
    try {
      await deleteAddress(addressData.id)
      setIsDeleteModalOpen(false)
      navigate('onBackToAddressList')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleOpenDeleteModalClick = () => setIsDeleteModalOpen(true)

  const handleDeleteModalClose = () => setIsDeleteModalOpen(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const activeElement = document.activeElement as HTMLElement
    if (activeElement && e.currentTarget.contains(activeElement)) {
      activeElement.blur()
    }
    if (!validateForm()) {
      buildingDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    const addressInput = getAddressInput()
    setAddress(addressInput)
    setIsLoading(true)
    try {
      if ('id' in addressInput) {
        await handleEditAddress(addressInput)
      } else {
        await handleCreateAddress(addressInput)
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    execWithErrorTracking(() => {
      addressDetailsEvent({
        addressDetailsSource: addressFormTrigger ?? 'PinOnMap',
        addressKind: addressData.kind,
        addressLine: addressData.addressLine,
        coordinatesLatitude: addressData.latitude,
        coordinatesLongitude: addressData.longitude,
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <LoaderWrapper loading={isLoading}>
      <form
        className={isMobile ? styles.addressDetailsMobile : styles.addressDetailsDesktop}
        onSubmit={handleSubmit}
      >
        {isMobile ? (
          <IconButton
            aria-label={t('glovo.common.back')}
            className={styles.backButton}
            data-testid="back-button"
            icon={ChevronBackwardIcon}
            size={IconButtonSize.small}
            variant={IconButtonVariants.floating}
            onClick={handleNavigateBack}
          />
        ) : (
          <TitleDesktop
            title={t('address-details.building-details.title')}
            onBackClick={handleNavigateBack}
          />
        )}
        <div className={styles.formContent}>
          <BuildingDetails
            address={addressData}
            buildingDetailsRef={buildingDetailsRef}
            fields={formData.fields}
            onFieldChange={handleFieldChange}
          />
          <RefineEntrance
            latitude={addressData.latitude}
            longitude={addressData.longitude}
            onSetEntrance={handleNavigationToSetEntrance}
          />
          <SaveAddressAs
            addressKind={addressData.kind}
            addressTag={addressData.tag}
            isAddressTagMissing={isAddressMissingTag}
            onTagChange={handleTagChange}
          />
        </div>
        <div className={styles.buttonContainer}>
          {isEditing && (
            <Button
              block
              label={t('address-details.delete-button')}
              type="button"
              variant={ButtonVariants.neutral}
              onClick={handleOpenDeleteModalClick}
            />
          )}
          <Button block label={t('address-details.confirm-button')} type="submit" />
        </div>

        {/* TODO: delete snackbar when no longer using iframe WEBX-461 */}
        {isMobile ? (
          <DeleteAddressModal
            isLoading={isDeleting}
            isOpen={isDeleteModalOpen}
            onClose={handleDeleteModalClose}
            onConfirmDelete={handleDelete}
          />
        ) : (
          isDeleteModalOpen && (
            <DeleteAddressSnackbar
              onClose={handleDeleteModalClose}
              onConfirmDelete={handleDelete}
            />
          )
        )}
      </form>
    </LoaderWrapper>
  )
}

export const AddressDetailsForm = withErrorBoundary(AddressDetailsFormComponent, {
  id: 'user.address_book.step.address_details_form',
  owner: Teams.WEB,
})
